import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Button, CircularLoader } from '@dhis2/ui';
import { FileIcon, XIcon, DownloadIcon } from 'lucide-react';
import { 
  saveFilesToDataStore, 
  loadFilesFromDataStore, 
  getFileStorageKey,
  initializeFileStorage 
} from '../utils/fileStorage';
import { logPPNAction } from '../utils/ppnLogStorage';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = [
  'image/jpeg', 'image/png', 'image/gif',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
];

export const FileAttachment = ({ 
  taskId, 
  selectedWeek, 
  objectiveId,
  engine, 
  onFileUploaded,
  onFileRemoved,
  isFormDisabled,
  showNotification,
  currentUser,
  isReadOnly = false,  
  compact = false      
}) => {
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const fileInputRef = useRef(null);

  // Load existing files
  const loadFiles = useCallback(async () => {
    if (!taskId || !objectiveId) {
      console.log('⚠️ FileAttachment: Missing taskId or objectiveId', { taskId, objectiveId });
      return;
    }
    try {
      const key = getFileStorageKey(objectiveId, taskId, selectedWeek);
      const loadedFiles = await loadFilesFromDataStore(engine, key);
      setFiles(loadedFiles);
    } catch (error) {
      console.log('No files found for this task');
    }
  }, [taskId, objectiveId, selectedWeek, engine]);

  // Handle file upload
  const handleFileUpload = useCallback(async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      showNotification(`File too large. Max size is ${MAX_FILE_SIZE / 1024 / 1024}MB`, 'error');
      return;
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      showNotification('File type not supported.', 'error');
      return;
    }

    setIsLoading(true);
    setUploadProgress({ [file.name]: 0 });

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const fileData = {
          id: `${file.name}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          name: file.name,
          type: file.type,
          size: file.size,
          data: e.target.result,
          uploadedAt: new Date().toISOString(),
          uploadedBy: {
            id: currentUser?.id || 'unknown',
            name: currentUser?.displayName || currentUser?.username || 'Unknown'
          }
        };

        const key = getFileStorageKey(objectiveId, taskId, selectedWeek);
        const updatedFiles = [...files, fileData];
        await saveFilesToDataStore(engine, key, updatedFiles);
        setFiles(updatedFiles);
        
        await logPPNAction(
          engine,
          objectiveId,
          selectedWeek,
          'file_uploaded',
          { 
            fileName: file.name, 
            fileSize: file.size,
            taskId: taskId,
            fileId: fileData.id 
          },
          currentUser
        );
        
        if (onFileUploaded) onFileUploaded(fileData);
        showNotification(`File "${file.name}" uploaded successfully`, 'success');
        setIsLoading(false);
        setUploadProgress({});
      };

      reader.onprogress = (e) => {
        if (e.lengthComputable) {
          const progress = Math.round((e.loaded / e.total) * 100);
          setUploadProgress({ [file.name]: progress });
        }
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error('File upload error:', error);
      showNotification('Failed to upload file', 'error');
      setIsLoading(false);
      setUploadProgress({});
    }

    event.target.value = null;
  }, [files, engine, objectiveId, taskId, selectedWeek, onFileUploaded, showNotification, currentUser]);

  // Handle file removal with logging and ownership check
  const handleRemoveFile = useCallback(async (fileId) => {
    const fileToRemove = files.find(f => f.id === fileId);
    if (!fileToRemove) return;

    // Check ownership
    const isOwner = fileToRemove.uploadedBy?.id === currentUser?.id;
    if (!isOwner) {
      showNotification('You are not allowed to delete this file.', 'error');
      return;
    }

    const updatedFiles = files.filter(f => f.id !== fileId);
    try {
      const key = getFileStorageKey(objectiveId, taskId, selectedWeek);
      await saveFilesToDataStore(engine, key, updatedFiles);
      setFiles(updatedFiles);
      
      await logPPNAction(
        engine,
        objectiveId,
        selectedWeek,
        'file_removed',
        { 
          fileName: fileToRemove.name,
          fileId: fileId,
          taskId: taskId 
        },
        currentUser
      );
      
      if (onFileRemoved) onFileRemoved(fileId);
      showNotification(`File "${fileToRemove.name}" removed`, 'success');
    } catch (error) {
      console.error('Failed to remove file:', error);
      showNotification('Failed to remove file', 'error');
    }
  }, [files, engine, objectiveId, taskId, selectedWeek, onFileRemoved, showNotification, currentUser]);

  // Load files on mount
  useEffect(() => {
    const init = async () => {
      await initializeFileStorage(engine);
      await loadFiles();
    };
    init();
  }, [loadFiles, engine]);

  // Handle file download
  const handleDownload = useCallback((file) => {
    try {
      const link = document.createElement('a');
      link.href = file.data;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Download error:', error);
      showNotification('Failed to download file', 'error');
    }
  }, [showNotification]);

  // Get file icon
  const getFileIcon = (fileType) => {
    if (fileType?.startsWith('image/')) return '🖼️';
    if (fileType === 'application/pdf') return '📄';
    if (fileType?.includes('word')) return '📝';
    if (fileType?.includes('excel')) return '📊';
    return '📎';
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // ─── READ‑ONLY VIEW ──────────────────────────────────────
  if (isReadOnly) {
    return (
      <div style={{ marginTop: '4px' }}>
        {files.length > 0 ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', fontSize: '0.75rem' }}>
            {files.map(file => (
              <div key={file.id} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '2px 8px',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
                border: '1px solid #e0e0e0'
              }}>
                <span>{getFileIcon(file.type)}</span>
                <a 
                  href={file.data} 
                  download={file.name}
                  style={{ color: '#1976d2', textDecoration: 'none', cursor: 'pointer' }}
                  onClick={(e) => {
                    e.preventDefault();
                    handleDownload(file);
                  }}
                >
                  {file.name}
                </a>
                <span style={{ fontSize: '0.6rem', color: '#999' }}>
                  ({formatFileSize(file.size)})
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: '0.75rem', color: '#999', fontStyle: 'italic', padding: '4px 0' }}>
            No files attached
          </div>
        )}
      </div>
    );
  }

  // ─── FORM DISABLED (hide completely) ──────────────────────
  if (isFormDisabled) {
    return null;
  }

  // ─── EDITABLE VIEW ──────────────────────────────────────
  return (
    <div style={{
      marginBottom: compact ? '8px' : '20px',
      border: '1px solid #e0e0e0',
      borderRadius: compact ? '6px' : '10px',
      overflow: 'hidden',
      backgroundColor: '#fafafa'
    }}>
      {/* Header - visible only when not compact */}
      {!compact && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#f5f5f5',
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '8px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '1.2rem' }}>📎</span>
            <span style={{ fontWeight: 600, color: '#333' }}>Attachments</span>
            <span style={{ 
              fontSize: '0.75rem', 
              color: '#666',
              backgroundColor: '#e0e0e0',
              padding: '2px 8px',
              borderRadius: '12px'
            }}>
              {files.length}
            </span>
          </div>
          
          <Button
            small
            onClick={() => fileInputRef.current?.click()}
            disabled={isLoading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              backgroundColor: '#4caf50',
              color: 'white',
              borderRadius: '6px',
              border: 'none',
              padding: '4px 12px'
            }}
          >
            {isLoading ? 'Uploading...' : '+ Add File'}
          </Button>
          
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileUpload}
            accept={ALLOWED_TYPES.join(',')}
            style={{ display: 'none' }}
          />
        </div>
      )}

      {/* File List */}
      <div style={{ padding: compact ? '8px 12px' : '12px 16px' }}>
        {isLoading && Object.keys(uploadProgress).length > 0 && (
          <div style={{
            padding: '8px',
            backgroundColor: '#e3f2fd',
            borderRadius: '6px',
            marginBottom: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <CircularLoader small />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '0.85rem' }}>
                Uploading: {Object.keys(uploadProgress)[0]}
              </div>
              <div style={{
                width: '100%',
                height: '4px',
                backgroundColor: '#e0e0e0',
                borderRadius: '2px',
                marginTop: '4px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${Object.values(uploadProgress)[0]}%`,
                  height: '100%',
                  backgroundColor: '#1976d2',
                  transition: 'width 0.3s'
                }} />
              </div>
            </div>
          </div>
        )}

        {/* Compact upload button - visible only in compact mode */}
        {compact && (
          <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'flex-end' }}>
           <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isLoading}
              style={{
                backgroundColor: '#4caf50',
                color: 'white',
                border: 'none',
                borderRadius: '9999px',          // full pill
                padding: '4px 14px',
                fontSize: '0.78rem',
                fontWeight: 500,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                opacity: isLoading ? 0.6 : 1
              }}
            >
              {isLoading ? 'Uploading...' : '+ Add File'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileUpload}
              accept={ALLOWED_TYPES.join(',')}
              style={{ display: 'none' }}
            />
          </div>
        )}

        {files.length === 0 && !isLoading && !compact && (
          <div style={{
            textAlign: 'center',
            padding: '24px 16px',
            color: '#999',
            fontSize: '0.9rem'
          }}>
            No files attached yet. Click "Add File" to upload evidence.
            <div style={{ fontSize: '0.75rem', color: '#bbb', marginTop: '4px' }}>
              Supports images, PDFs, Word, and Excel files (max 10MB)
            </div>
          </div>
        )}

        {files.length === 0 && !isLoading && compact && (
          <div style={{
            textAlign: 'center',
            padding: '8px',
            color: '#999',
            fontSize: '0.8rem'
          }}>
            No files attached
          </div>
        )}

        {files.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {files.map((file) => {
              const isOwner = file.uploadedBy?.id === currentUser?.id;

              return (
                <div
                  key={file.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: compact ? '4px 8px' : '8px 12px',
                    backgroundColor: 'white',
                    borderRadius: '6px',
                    border: '1px solid #e8e8e8',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#1976d2';
                    e.currentTarget.style.backgroundColor = '#f5f9ff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e8e8e8';
                    e.currentTarget.style.backgroundColor = 'white';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                    <span style={{ fontSize: compact ? '1rem' : '1.5rem' }}>
                      {getFileIcon(file.type)}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontWeight: 500,
                        color: '#333',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontSize: compact ? '0.75rem' : '0.85rem'
                      }}>
                        {file.name}
                      </div>
                      <div style={{
                        fontSize: compact ? '0.6rem' : '0.7rem',
                        color: '#888',
                        marginTop: '2px'
                      }}>
                        {formatFileSize(file.size)} • {new Date(file.uploadedAt).toLocaleDateString()}
                        {file.uploadedBy?.name && ` • by ${file.uploadedBy.name}`}
                      </div>
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <Button
                      small
                      onClick={() => handleDownload(file)}
                      style={{
                        backgroundColor: 'transparent',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        padding: compact ? '2px 4px' : '2px 6px',
                        color: '#555',
                        fontSize: compact ? '0.7rem' : '0.8rem'
                      }}
                    >
                      <DownloadIcon size={compact ? 12 : 14} />
                    </Button>

                    {isOwner && (
                      <Button
                        small
                        destructive
                        onClick={() => handleRemoveFile(file.id)}
                        style={{
                          backgroundColor: 'transparent',
                          border: '1px solid #f5c6cb',
                          borderRadius: '4px',
                          padding: compact ? '2px 4px' : '2px 6px',
                          color: '#dc3545',
                          fontSize: compact ? '0.7rem' : '0.8rem'
                        }}
                      >
                        <XIcon size={compact ? 12 : 14} />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default FileAttachment;